import React, { useState, useRef } from 'react';
import Box from '../../components/Common/Box';
import StockSearch from '../../components/Common/StockSearch';

interface DesktopTransactionsProps {
    activeTab: 'buy' | 'sell' | 'history';
    setActiveTab: (tab: 'buy' | 'sell' | 'history') => void;
    balance: number;
    stocks: any[];
    portfolio: any[];
    transactions: any[];
    // Form State
    selectedStock: string;
    quantity: string;
    price: string;
    date: string;
    loading: boolean;
    error: string;
    success: string;
    feeRate: string;
    totalCost: string;
    finalUnitPrice: number;
    selectedHolding: any;
    // Handlers
    setDate: (val: string) => void;
    handleStockChange: (val: string) => void;
    setQuantity: (val: string) => void;
    setPrice: (val: string) => void;
    setFeeRate: (val: string) => void;
    saveFeeRate: () => void;
    handleBuy: (e: React.FormEvent) => void;
    handleSell: (e: React.FormEvent) => void;
    handleExport: (format: string) => void;
    onImportSuccess?: () => void;
    apiUrl: string;
    token: string;
    // Filters State
    filterType: 'ALL' | 'BUY' | 'SELL';
    setFilterType: (val: 'ALL' | 'BUY' | 'SELL') => void;
    filterSymbol: string;
    setFilterSymbol: (val: string) => void;
    filterDateFrom: string;
    setFilterDateFrom: (val: string) => void;
    filterDateTo: string;
    setFilterDateTo: (val: string) => void;
}

// ── Import SGI Modal ──────────────────────────────────────────
type ImportStep = 'select' | 'preview' | 'result';
interface ImportPreviewItem {
    row: number; symbol: string; company: string; type: string;
    quantity: number; price_brut: number; price: number; fees: number;
    fee_rate: number; total: number; date: string;
    order_ref: string; price_source: string;
}
interface ImportResult {
    dry_run: boolean; to_import?: number; imported?: number;
    skipped: number; duplicates: number; errors: number;
    preview?: ImportPreviewItem[];
    transactions?: ImportPreviewItem[];
    skipped_details: any[];
    duplicate_details: any[];
    error_details: any[];
    current_balance?: number;
    total_buy_cost?: number;
    required_deposit?: number;
    insufficient_at?: string;
}

const ImportModal: React.FC<{
    onClose: () => void;
    apiUrl: string;
    token: string;
    onSuccess: () => void;
}> = ({ onClose, apiUrl, token, onSuccess }) => {
    const [step, setStep] = useState<ImportStep>('select');
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [preview, setPreview] = useState<ImportResult | null>(null);
    const [result, setResult] = useState<ImportResult | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0] || null;
        setFile(f);
    };

    const handlePreview = async () => {
        if (!file) return;
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('dry_run', 'true');
            const res = await fetch(`${apiUrl}/api/transactions/import-csv`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: fd,
            });
            const data: ImportResult = await res.json();
            if (!res.ok) throw new Error((data as any).detail || 'Erreur de parsing');
            setPreview(data);
            setStep('preview');
        } catch (err: any) {
            alert(`Erreur : ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async () => {
        if (!file) return;
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('dry_run', 'false');
            const res = await fetch(`${apiUrl}/api/transactions/import-csv`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: fd,
            });
            const data: ImportResult = await res.json();
            if (!res.ok) throw new Error((data as any).detail || 'Erreur import');
            setResult(data);
            setStep('result');
            onSuccess();
        } catch (err: any) {
            alert(`Erreur : ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const overlay: React.CSSProperties = {
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
    };
    const modal: React.CSSProperties = {
        background: 'var(--bg-box)', borderRadius: '12px', padding: '28px',
        width: '100%', maxWidth: '760px', maxHeight: '85vh',
        overflowY: 'auto', border: '1px solid var(--border-color)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
    };

    return (
        <div style={overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div style={modal}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: 'var(--text-heading)' }}>
                            📥 Import CSV — Ordres SGI
                        </h2>
                        <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-muted)' }}>
                            {step === 'select' && 'Sélectionnez votre export SGI'}
                            {step === 'preview' && `${preview?.to_import} ordres prêts à importer`}
                            {step === 'result' && 'Import terminé'}
                        </p>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1 }}>✕</button>
                </div>

                {/* Step 1: Select file */}
                {step === 'select' && (
                    <div>
                        <div style={{ padding: '16px', background: 'rgba(59,130,246,0.08)', borderRadius: '8px', border: '1px solid rgba(59,130,246,0.2)', marginBottom: '20px', fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                            <strong style={{ color: '#3b82f6' }}>📋 Format attendu :</strong> Export CSV/TSV de votre SGI BRVM.<br/>
                            Colonnes requises : <code>Libellé</code>, <code>Sens</code> (A/V), <code>Qte. Exec.</code>, <code>Cours</code>, <code>PMUP</code>, <code>Date heure</code>.<br/>
                            <span style={{ color: '#f59e0b' }}>⚠️ L'import ne modifie pas votre solde simulé.</span>
                        </div>

                        <div
                            onClick={() => fileRef.current?.click()}
                            style={{
                                border: `2px dashed ${file ? '#10b981' : 'var(--border-color)'}`,
                                borderRadius: '10px', padding: '40px 20px', textAlign: 'center',
                                cursor: 'pointer', background: file ? 'rgba(16,185,129,0.05)' : 'var(--bg-input)',
                                transition: 'all 0.2s',
                            }}
                        >
                            <div style={{ fontSize: '36px', marginBottom: '8px' }}>{file ? '✅' : '📁'}</div>
                            <div style={{ fontWeight: 600, color: 'var(--text-main)', marginBottom: '4px' }}>
                                {file ? file.name : 'Cliquer pour sélectionner le fichier CSV'}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                {file ? `${(file.size / 1024).toFixed(1)} KB` : 'Format : .csv ou .txt (tabulation ou point-virgule)'}
                            </div>
                        </div>
                        <input ref={fileRef} type="file" accept=".csv,.txt,.tsv" style={{ display: 'none' }} onChange={handleFileChange} />

                        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
                            <button onClick={onClose} style={{ padding: '10px 20px', background: 'var(--bg-input)', color: 'var(--text-main)', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>Annuler</button>
                            <button
                                onClick={handlePreview}
                                disabled={!file || loading}
                                style={{ padding: '10px 20px', background: file ? '#3b82f6' : '#6b7280', color: 'white', border: 'none', borderRadius: '6px', cursor: file ? 'pointer' : 'not-allowed', fontWeight: 600 }}
                            >
                                {loading ? 'Analyse...' : 'Analyser →'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 2: Preview */}
                {step === 'preview' && preview && (
                    <div>
                        {/* Summary badges */}
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                            {[
                                { label: `${preview.to_import} à importer`, color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
                                { label: `${preview.duplicates} doublons`, color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' },
                                { label: `${preview.skipped} ignorés`, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
                                { label: `${preview.errors} erreurs`, color: '#ef4444', bg: 'rgba(239,68,68,0.1)' },
                            ].map(b => (
                                <span key={b.label} style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 700, color: b.color, background: b.bg }}>{b.label}</span>
                            ))}
                        </div>

                        {/* Balance Warning */}
                        {preview.required_deposit !== undefined && preview.required_deposit > 0 && (
                            <div style={{ padding: '12px 16px', background: 'rgba(239,68,68,0.08)', borderRadius: '8px', border: '1px solid rgba(239,68,68,0.2)', marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <div style={{ fontSize: '24px' }}>⚠️</div>
                                <div style={{ fontSize: '13px', color: '#b91c1c' }}>
                                    <strong style={{ display: 'block', marginBottom: '2px' }}>Ajustement de solde requis</strong>
                                    L'analyse chronologique indique qu'un complément de <strong>{preview.required_deposit.toLocaleString('fr-FR')} FCFA</strong> est nécessaire pour couvrir ces opérations 
                                    {preview.insufficient_at && ` (premier défaut détecté le ${new Date(preview.insufficient_at).toLocaleDateString('fr-FR')})`}.
                                    Veuillez créditer votre compte avant de confirmer.
                                </div>
                            </div>
                        )}

                        {/* Preview table */}
                        <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                    <tr style={{ background: 'var(--bg-input)' }}>
                                        {['Symbole', 'Sens', 'Qté', 'Cours SGI', 'PRU (avec frais)', 'Total', 'Date', 'Réf'].map(h => (
                                            <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)', whiteSpace: 'nowrap' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {(preview.preview || []).map((item, idx) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                            <td style={{ padding: '7px 12px', fontWeight: 700, color: 'var(--text-heading)' }}>{item.symbol}</td>
                                            <td style={{ padding: '7px 12px' }}>
                                                <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700,
                                                    background: item.type === 'BUY' ? '#e8f5e9' : '#ffebee',
                                                    color: item.type === 'BUY' ? '#2e7d32' : '#c62828' }}>
                                                    {item.type === 'BUY' ? 'ACHAT' : 'VENTE'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '7px 12px' }}>{item.quantity}</td>
                                            <td style={{ padding: '7px 12px', color: 'var(--text-muted)' }}>
                                                {item.price_brut.toLocaleString('fr-FR')} FCFA
                                                {item.price_source === 'PMUP' && <span style={{ fontSize: '10px', color: '#f59e0b', marginLeft: '4px' }}>PMUP</span>}
                                            </td>
                                            <td style={{ padding: '7px 12px' }}>
                                                <div style={{ fontWeight: 600 }}>{item.price.toLocaleString('fr-FR')} FCFA</div>
                                                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '1px' }}>Frais {item.fee_rate}% = +{item.fees.toLocaleString('fr-FR')} FCFA</div>
                                            </td>
                                            <td style={{ padding: '7px 12px', fontWeight: 600 }}>{item.total.toLocaleString('fr-FR')} FCFA</td>
                                            <td style={{ padding: '7px 12px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{new Date(item.date).toLocaleDateString('fr-FR')}</td>
                                            <td style={{ padding: '7px 12px', color: 'var(--text-muted)', fontSize: '11px' }}>{item.order_ref}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Skipped/errors details */}
                        {preview.duplicate_details.length > 0 && (
                            <details style={{ marginTop: '12px', fontSize: '12px', color: '#3b82f6' }}>
                                <summary style={{ cursor: 'pointer' }}>Doublons détectés ({preview.duplicate_details.length})</summary>
                                <ul style={{ margin: '6px 0 0 16px' }}>{preview.duplicate_details.map((s, i) => <li key={i}>{s.symbol} — Ordre #{s.order_ref} déjà présent</li>)}</ul>
                            </details>
                        )}
                        {preview.skipped_details.length > 0 && (
                            <details style={{ marginTop: '8px', fontSize: '12px', color: '#f59e0b' }}>
                                <summary style={{ cursor: 'pointer' }}>Lignes ignorées ({preview.skipped_details.length})</summary>
                                <ul style={{ margin: '6px 0 0 16px' }}>{preview.skipped_details.map((s, i) => <li key={i}>{s.symbol ? `${s.symbol} — ` : ''}{s.reason}</li>)}</ul>
                            </details>
                        )}
                        {preview.error_details.length > 0 && (
                            <details style={{ marginTop: '8px', fontSize: '12px', color: '#ef4444' }}>
                                <summary style={{ cursor: 'pointer' }}>Erreurs ({preview.error_details.length})</summary>
                                <ul style={{ margin: '6px 0 0 16px' }}>{preview.error_details.map((e, i) => <li key={i}>{e.symbol ? `${e.symbol} — ` : ''}{e.reason}</li>)}</ul>
                            </details>
                        )}

                        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setStep('select')} style={{ padding: '10px 20px', background: 'var(--bg-input)', color: 'var(--text-main)', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>← Retour</button>
                            <button
                                onClick={handleConfirm}
                                disabled={loading || (preview.to_import ?? 0) === 0}
                                style={{ padding: '10px 24px', background: (preview.to_import ?? 0) > 0 ? '#10b981' : '#6b7280', color: 'white', border: 'none', borderRadius: '6px', cursor: (preview.to_import ?? 0) > 0 ? 'pointer' : 'not-allowed', fontWeight: 700 }}
                            >
                                {loading ? 'Import en cours...' : `✅ Confirmer l'import (${preview.to_import})`}
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 3: Result */}
                {step === 'result' && result && (
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '56px', marginBottom: '12px' }}>🎉</div>
                        <h3 style={{ margin: '0 0 8px', color: 'var(--text-heading)' }}>Import terminé !</h3>
                        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '20px' }}>
                            <div style={{ padding: '16px 24px', background: 'rgba(16,185,129,0.1)', borderRadius: '10px', minWidth: '100px' }}>
                                <div style={{ fontSize: '28px', fontWeight: 800, color: '#10b981' }}>{result.imported}</div>
                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>importés</div>
                            </div>
                            <div style={{ padding: '16px 24px', background: 'rgba(59,130,246,0.1)', borderRadius: '10px', minWidth: '100px' }}>
                                <div style={{ fontSize: '28px', fontWeight: 800, color: '#3b82f6' }}>{result.duplicates}</div>
                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>doublons</div>
                            </div>
                            <div style={{ padding: '16px 24px', background: 'rgba(245,158,11,0.1)', borderRadius: '10px', minWidth: '100px' }}>
                                <div style={{ fontSize: '28px', fontWeight: 800, color: '#f59e0b' }}>{result.skipped}</div>
                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>ignorés</div>
                            </div>
                            <div style={{ padding: '16px 24px', background: 'rgba(239,68,68,0.1)', borderRadius: '10px', minWidth: '100px' }}>
                                <div style={{ fontSize: '28px', fontWeight: 800, color: '#ef4444' }}>{result.errors}</div>
                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>erreurs</div>
                            </div>
                        </div>
                        <p style={{ marginTop: '16px', fontSize: '13px', color: 'var(--text-muted)' }}>
                            ⚠️ Votre solde simulé n'a pas été modifié. Ajustez-le manuellement si nécessaire.
                        </p>
                        <button onClick={onClose} style={{ marginTop: '20px', padding: '12px 32px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '15px' }}>Fermer</button>
                    </div>
                )}
            </div>
        </div>
    );
};


const DesktopTransactions: React.FC<DesktopTransactionsProps> = ({
    activeTab, setActiveTab, balance, stocks, portfolio, transactions,
    selectedStock, quantity, price, date, loading, error, success, feeRate, totalCost, finalUnitPrice, selectedHolding,
    setDate, handleStockChange, setQuantity, setPrice, setFeeRate, saveFeeRate, handleBuy, handleSell, handleExport,
    filterType, setFilterType, filterSymbol, setFilterSymbol, filterDateFrom, setFilterDateFrom, filterDateTo, setFilterDateTo,
    onImportSuccess, apiUrl, token,
}) => {
    const [showImportModal, setShowImportModal] = useState(false);
    return (
        <>
            <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: 'var(--bg-box)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>
                    Solde disponible: <strong style={{ color: '#4CAF50', fontSize: '18px' }}>{balance.toLocaleString()} FCFA</strong>
                </p>
            </div>

            <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={() => setActiveTab('buy')} style={{ flex: 1, padding: '12px', backgroundColor: activeTab === 'buy' ? '#4CAF50' : 'var(--bg-input)', color: activeTab === 'buy' ? 'white' : 'var(--text-main)', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Simuler Achat</button>
                <button onClick={() => setActiveTab('sell')} style={{ flex: 1, padding: '12px', backgroundColor: activeTab === 'sell' ? '#f44336' : 'var(--bg-input)', color: activeTab === 'sell' ? 'white' : 'var(--text-main)', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Simuler Vente</button>
                <button onClick={() => setActiveTab('history')} style={{ flex: 1, padding: '12px', backgroundColor: activeTab === 'history' ? '#2196F3' : 'var(--bg-input)', color: activeTab === 'history' ? 'white' : 'var(--text-main)', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Historique</button>
            </div>

            {(activeTab === 'buy' || activeTab === 'sell') && (
                <Box>
                    <div className='box-title box-vertical-padding box-horizontal-padding'>
                        <p>{activeTab === 'buy' ? 'Simuler un achat d\'actions' : 'Simuler une vente d\'actions'}</p>
                    </div>
                    <div className='box-content box-horizontal-padding' style={{ padding: '20px' }}>
                        <form onSubmit={activeTab === 'buy' ? handleBuy : handleSell}>
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Date de transaction</label>
                                <input type='date' value={date} onChange={(e) => setDate(e.target.value)} style={{ width: '100%', padding: '12px', fontSize: '16px', border: '1px solid var(--border-color)', borderRadius: '4px', backgroundColor: 'var(--bg-input)', color: 'var(--text-main)' }} disabled={loading} required />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                                    {activeTab === 'buy' ? 'Rechercher une société' : 'Sélectionner depuis votre portefeuille'}
                                </label>
                                <StockSearch
                                    stocks={activeTab === 'buy' ? stocks : portfolio.map(item => {
                                        const s = stocks.find(st => st.symbole === item.stock_symbol);
                                        return { symbole: item.stock_symbol, nom_societe: s ? s.nom_societe : `Stock ${item.stock_symbol} ` };
                                    })}
                                    value={selectedStock}
                                    onChange={handleStockChange}
                                    placeholder={activeTab === 'buy' ? "Rechercher une action..." : "Rechercher dans votre portefeuille..."}
                                    disabled={loading}
                                />
                            </div>

                            {activeTab === 'sell' && selectedHolding && (
                                <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
                                    <p style={{ margin: 0, fontSize: '14px', color: '#856404' }}>Quantité disponible: <strong>{selectedHolding.quantity}</strong></p>
                                </div>
                            )}

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Quantité</label>
                                <input type='number' value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder='Ex: 10' min='1' style={{ width: '100%', padding: '12px', fontSize: '16px', border: '1px solid var(--border-color)', borderRadius: '4px', backgroundColor: 'var(--bg-input)', color: 'var(--text-main)' }} disabled={loading} />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Prix unitaire (FCFA)</label>
                                <input type='number' value={price} onChange={(e) => setPrice(e.target.value)} placeholder='Ex: 5000' min='0.01' step='0.01' style={{ width: '100%', padding: '12px', fontSize: '16px', border: '1px solid var(--border-color)', borderRadius: '4px', backgroundColor: 'var(--bg-input)', color: 'var(--text-main)' }} disabled={loading} />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Frais de transaction (%)</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <input type='number' value={feeRate} onChange={(e) => setFeeRate(e.target.value)} placeholder='Ex: 1.1' min='0' step='0.1' style={{ width: '100%', padding: '12px', fontSize: '16px', border: '1px solid var(--border-color)', borderRadius: '4px', backgroundColor: 'var(--bg-input)', color: 'var(--text-main)' }} disabled={loading} onBlur={saveFeeRate} />
                                    <div style={{ fontSize: '13px', color: 'var(--text-muted)', width: '200px' }}>
                                        {activeTab === 'buy' ? `PRU calculé: ${finalUnitPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })} ` : `Net vendeur: ${finalUnitPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })} `}
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'var(--bg-box)', borderRadius: '4px', border: '1px solid var(--border-color)' }}>
                                <p style={{ margin: 0, fontSize: '16px' }}>
                                    {activeTab === 'buy' ? 'Coût total' : 'Montant à recevoir'}: <strong style={{ fontSize: '20px', color: activeTab === 'buy' ? '#f44336' : '#4CAF50' }}>{totalCost} FCFA</strong>
                                </p>
                            </div>

                            {error && <div style={{ padding: '12px', marginBottom: '20px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px' }}>{error}</div>}
                            {success && <div style={{ padding: '12px', marginBottom: '20px', backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '4px' }}>{success}</div>}

                            <button type='submit' disabled={loading} style={{ width: '100%', padding: '14px', fontSize: '16px', fontWeight: 'bold', backgroundColor: loading ? '#ccc' : activeTab === 'buy' ? '#4CAF50' : '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: loading ? 'not-allowed' : 'pointer' }}>
                                {loading ? 'Traitement...' : activeTab === 'buy' ? 'Valider l\'Achat Simulé' : 'Valider la Vente Simulée'}
                            </button>
                        </form>
                    </div>
                </Box>
            )}

            {activeTab === 'history' && (
                <Box>
                    <div className='box-title box-vertical-padding box-horizontal-padding'>
                        <p>Historique des transactions</p>
                    </div>

                    <div style={{ padding: '15px 20px', backgroundColor: 'var(--bg-box)', borderBottom: '1px solid var(--border-color)', display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: '5px' }}>
                            {(['ALL', 'BUY', 'SELL'] as const).map((type) => (
                                <button
                                    key={type} onClick={() => setFilterType(type)}
                                    style={{
                                        padding: '6px 12px', fontSize: '12px', fontWeight: '600',
                                        border: filterType === type ? '1px solid transparent' : '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer',
                                        backgroundColor: filterType === type ? (type === 'BUY' ? '#4CAF50' : type === 'SELL' ? '#f44336' : '#2196F3') : 'var(--bg-input)',
                                        color: filterType === type ? 'white' : 'var(--text-main)',
                                    }}
                                >
                                    {type === 'ALL' ? 'Tous' : type === 'BUY' ? 'Achats' : 'Ventes'}
                                </button>
                            ))}
                        </div>

                        <select
                            value={filterSymbol} onChange={(e) => setFilterSymbol(e.target.value)}
                            style={{ padding: '6px 10px', fontSize: '13px', border: '1px solid var(--border-color)', borderRadius: '4px', minWidth: '120px', backgroundColor: 'var(--bg-input)', color: 'var(--text-main)' }}
                        >
                            <option value="">Toutes les actions</option>
                            {[...new Set(transactions.map(t => t.stock_symbol).filter(Boolean))].sort().map(symbol => (
                                <option key={symbol} value={symbol}>{symbol}</option>
                            ))}
                        </select>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Du:</span>
                            <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} style={{ padding: '5px 8px', fontSize: '12px', border: '1px solid var(--border-color)', borderRadius: '4px', backgroundColor: 'var(--bg-input)', color: 'var(--text-main)' }} />
                            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Au:</span>
                            <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} style={{ padding: '5px 8px', fontSize: '12px', border: '1px solid var(--border-color)', borderRadius: '4px', backgroundColor: 'var(--bg-input)', color: 'var(--text-main)' }} />
                        </div>

                        {(filterType !== 'ALL' || filterSymbol || filterDateFrom || filterDateTo) && (
                            <button
                                onClick={() => { setFilterType('ALL'); setFilterSymbol(''); setFilterDateFrom(''); setFilterDateTo(''); }}
                                style={{ padding: '6px 12px', fontSize: '12px', color: 'var(--text-main)', backgroundColor: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer' }}
                            >
                                ✕ Réinitialiser
                            </button>
                        )}

                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                            <button
                                onClick={() => setShowImportModal(true)}
                                style={{ padding: '8px 14px', fontSize: '13px', fontWeight: 'bold', backgroundColor: '#7c3aed', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                                <span className="material-icons" style={{ fontSize: '16px' }}>upload</span> Import SGI
                            </button>
                            <button
                                onClick={() => handleExport('csv')}
                                style={{ padding: '8px 16px', fontSize: '13px', fontWeight: 'bold', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                                <span className="material-icons" style={{ fontSize: '16px' }}>download</span> Export CSV
                            </button>
                        </div>
                    </div>

                    <div className='box-content'>
                        {(() => {
                            const filtered = transactions.filter(tx => {
                                if (filterType !== 'ALL' && tx.transaction_type !== filterType) return false;
                                if (filterSymbol && tx.stock_symbol !== filterSymbol) return false;
                                if (filterDateFrom && tx.transaction_date < filterDateFrom) return false;
                                if (filterDateTo && tx.transaction_date.split('T')[0] > filterDateTo) return false;
                                return true;
                            });

                            const buyTotal = filtered.filter(t => t.transaction_type === 'BUY').reduce((sum, t) => sum + t.total_amount, 0);
                            const sellTotal = filtered.filter(t => t.transaction_type === 'SELL').reduce((sum, t) => sum + t.total_amount, 0);

                            const currentYear = new Date().getFullYear();
                            const yearStart = `${currentYear}-01-01`;
                            const ytdTransactions = transactions.filter(t => t.transaction_date >= yearStart);
                            const buyTotalYTD = ytdTransactions.filter(t => t.transaction_type === 'BUY').reduce((sum, t) => sum + t.total_amount, 0);
                            const sellTotalYTD = ytdTransactions.filter(t => t.transaction_type === 'SELL').reduce((sum, t) => sum + t.total_amount, 0);

                            return filtered.length > 0 ? (
                                <>
                                    <div style={{ padding: '12px 20px', backgroundColor: 'var(--bg-box)', borderBottom: '1px solid var(--border-color)', display: 'flex', flexWrap: 'wrap', gap: '20px', fontSize: '13px' }}>
                                        <span><strong>{filtered.length}</strong> transaction(s)</span>
                                        <span style={{ color: '#4CAF50' }}>Achats: <strong>{buyTotal.toLocaleString()} FCFA</strong></span>
                                        <span style={{ color: '#f44336' }}>Ventes: <strong>{sellTotal.toLocaleString()} FCFA</strong></span>
                                        <span style={{ borderLeft: '1px solid var(--border-color)', paddingLeft: '20px', color: '#4CAF50' }}>Achats YTD: <strong>{buyTotalYTD.toLocaleString()} FCFA</strong></span>
                                        <span style={{ color: '#f44336' }}>Ventes YTD: <strong>{sellTotalYTD.toLocaleString()} FCFA</strong></span>
                                    </div>

                                    <table className='data-table'>
                                        <thead>
                                            <tr>
                                                <th className='left'>Date</th>
                                                <th className='left'>Type</th>
                                                <th className='left'>Action</th>
                                                <th className='center'>Quantité</th>
                                                <th className='center'>Prix unitaire</th>
                                                <th className='center'>Montant total</th>
                                                <th className='center'>Statut</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filtered.map((tx) => (
                                                <tr key={tx.id}>
                                                    <td>{new Date(tx.transaction_date).toLocaleString('fr-FR')}</td>
                                                    <td>
                                                        {(() => {
                                                            const t = tx.transaction_type;
                                                            const cfg: Record<string, { label: string; bg: string; color: string }> = {
                                                                BUY:    { label: 'ACHAT',   bg: '#e8f5e9', color: '#2e7d32' },
                                                                SELL:   { label: 'VENTE',   bg: '#ffebee', color: '#c62828' },
                                                                CREDIT: { label: 'DÉPÔT',   bg: '#e3f2fd', color: '#1565c0' },
                                                                DEBIT:  { label: 'RETRAIT', bg: '#fff3e0', color: '#e65100' },
                                                                SPLIT:  { label: 'FRACTIONNEMENT', bg: '#f3e5f5', color: '#6a1b9a' },
                                                            };
                                                            const style = cfg[t] || { label: t, bg: 'var(--bg-hover)', color: 'var(--text-muted)' };
                                                            return (
                                                                <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: style.bg, color: style.color, fontWeight: 'bold', fontSize: '11px' }}>
                                                                    {style.label}
                                                                </span>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td><strong>{tx.stock_symbol || '—'}</strong></td>
                                                    <td className='center'>{tx.quantity ?? '—'}</td>
                                                    <td className='center'>{tx.price_per_unit != null ? tx.price_per_unit.toLocaleString() + ' FCFA' : '—'}</td>
                                                    <td className='center'><strong>{tx.total_amount.toLocaleString()} FCFA</strong></td>
                                                    <td className='center'>{({ COMPLETED: 'Exécutée', PENDING: 'En attente', CANCELLED: 'Annulée' } as Record<string, string>)[tx.status] ?? tx.status}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            ) : (
                                <p style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                                    {transactions.length > 0 ? 'Aucune transaction correspondant aux filtres' : 'Aucune transaction'}
                                </p>
                            );
                        })()}
                    </div>
                </Box>
            )}
            {showImportModal && (
                <ImportModal
                    onClose={() => setShowImportModal(false)}
                    apiUrl={apiUrl}
                    token={token}
                    onSuccess={() => {
                        setShowImportModal(false);
                        onImportSuccess?.();
                    }}
                />
            )}
        </>
    );
};

export default DesktopTransactions;
